import {hexToRgb, rgbToHex} from '@shortsworks/ui/components/ColorGenerator/GradientGenerator/util/color';

export interface GradientColorStop {
  color: string;
  offset: number;
}

const _sortByOffset = (a: GradientColorStop, b: GradientColorStop) => a.offset - b.offset;



export enum GradientType {
  Linear = 'linear',
  Radial = 'radial',
  Conic = 'conic',
}

export class GradientColor {
  type: GradientType;
  colorStops: GradientColorStop[] = [];
  angle: number = 0;

  constructor(type: GradientType, colorStops: GradientColorStop[] = [], angle = 0) {
    this.type = type;
    this.colorStops = colorStops;
    this.angle = angle;
  }

  static new(type: GradientType, colorStops: GradientColorStop[] = [], angle = 0): GradientColor {
    return new GradientColor(type, colorStops, angle);
  }

  static fromCss(css: string): GradientColor | null {
    const linearGradientRegex = /linear-gradient\((\d+)deg, (.+)\)/;
    const match = css.match(linearGradientRegex);

    if (!match) {
      // throw new Error('Invalid CSS Gradient format');
      return null;
    }

    const angle = parseFloat(match[1]!);
    const colorStopStrings = match[2]!.split(',');

    const colorStops = colorStopStrings.map(colorStopString => {
      const [color, position] = colorStopString.trim().split(' ');
      return {
        color: color!,
        offset: parseFloat(position!) / 100,
      };
    });

    return new GradientColor(GradientType.Linear, colorStops!, angle);
  }

  public clone = (): GradientColor => {
    return new GradientColor(this.type, this.colorStops.slice(), this.angle);
  }

  public cloneWithAngle = (angle: number): GradientColor => {
    return new GradientColor(this.type, this.colorStops.slice(), angle);
  }

  public addColorWithRandom = (offset: number): string => {
    const newColor = this.calculateAverageColor();
    this.colorStops.push({ color: newColor, offset });
    return newColor;
  }

  public removeColor = (index: number) => {
    this.colorStops.splice(index, 1);
  }

  public getSortedColors = (): GradientColorStop[] => {
    return this.colorStops.slice().sort(_sortByOffset);
  }

  public toCss = (option?: {type: GradientType; angle: number}): string => {
    const { type, colorStops, angle } = this;
    const targetType = option?.type || type;
    const targetAngle = option?.angle || angle;

    switch (targetType) {
      case GradientType.Linear:
        return `linear-gradient(${targetAngle}deg, ${(colorStops as GradientColorStop[]).slice().sort(_sortByOffset).map((c) => `${c.color} ${c.offset * 100}%`).join(', ')})`;
      case GradientType.Radial:
        return `radial-gradient(${(colorStops as GradientColorStop[]).slice().sort(_sortByOffset).map((c) => `${c.color} ${c.offset * 100}%`).join(', ')})`;
      case GradientType.Conic:
        return `conic-gradient(${targetAngle}deg, ${(colorStops as GradientColorStop[]).slice().sort(_sortByOffset).map((c) => `${c.color} ${c.offset * 100}%`).join(', ')})`;
    }
  }

  private calculateAverageColor = (): string => {
    let totalR = 0;
    let totalG = 0;
    let totalB = 0;

    this.colorStops.forEach(item => {
      const { r, g, b } = hexToRgb(item.color);
      totalR += r;
      totalG += g;
      totalB += b;
    });

    const length = this.colorStops.length;
    const averageR = Math.round(totalR / length);
    const averageG = Math.round(totalG / length);
    const averageB = Math.round(totalB / length);

    return rgbToHex(averageR, averageG, averageB);
  }
}
